import type { CompetitorProfileProps } from './competitor-profile.types';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { countryLabel } from './country-label';

export function CompetitorProfile({
  competitor: comp,
}: CompetitorProfileProps) {
  const profile = comp.startggProfile;
  const first = profile?.participants[0];
  return (
    <div className="min-w-0 flex-1 space-y-2">
      <div className="flex items-center gap-3">
        <Avatar className="h-10 w-10 rounded-md">
          <AvatarImage
            src={comp.imageUrl || first?.avatarUrl || undefined}
            alt={comp.name}
          />
          <AvatarFallback>{comp.name.charAt(0).toUpperCase()}</AvatarFallback>
        </Avatar>
        <div className="min-w-0">
          <div className="font-medium break-words">
            {first?.prefix && (
              <span className="text-muted-foreground">{first.prefix} · </span>
            )}
            {comp.name}
          </div>
          <div className="text-xs text-muted-foreground flex flex-wrap gap-x-3">
            <span>
              {profile
                ? profile.seed
                  ? `Official seed #${profile.seed}`
                  : 'Unseeded'
                : comp.seed > 0
                  ? `Seed #${comp.seed}`
                  : 'Unseeded'}
            </span>
            {first?.country && <span>{countryLabel(first.country)}</span>}
            {first?.profileUrl && (
              <a
                className="text-primary underline"
                href={first.profileUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                start.gg ↗
              </a>
            )}
            {comp.withdrawn && (
              <span className="text-destructive">Withdrawn from roster</span>
            )}
            {profile?.disqualified && (
              <span className="text-destructive">Disqualified</span>
            )}
            {profile?.placement != null && (
              <span>
                {profile.placementFinal
                  ? 'Final placement'
                  : 'Current placement'}{' '}
                #{profile.placement}
              </span>
            )}
          </div>
        </div>
      </div>
      {profile && (
        <details open className="text-sm">
          <summary className="cursor-pointer text-muted-foreground">
            Player details
            {profile.participants.length > 1
              ? ` (${profile.participants.length} members)`
              : ''}
          </summary>
          <div className="space-y-4 pt-3">
            {profile.participants.map((p) => (
              <div key={p.id} className="space-y-2 border-l pl-3">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-medium">{p.gamerTag}</span>
                  {p.pronouns && (
                    <span className="text-muted-foreground">
                      ({p.pronouns})
                    </span>
                  )}
                  {p.country && <span>{countryLabel(p.country)}</span>}
                  {p.checkedIn != null && (
                    <span className="text-muted-foreground">
                      {p.checkedIn ? 'Checked in' : 'Not checked in'}
                    </span>
                  )}
                  {p.profileUrl && (
                    <a
                      className="text-primary underline"
                      href={p.profileUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      start.gg profile ↗
                    </a>
                  )}
                </div>
                {p.bio && (
                  <p className="whitespace-pre-wrap break-words text-muted-foreground">
                    {p.bio}
                  </p>
                )}
                <div className="flex flex-wrap gap-3">
                  {p.socials.map((s, i) =>
                    s.url ? (
                      <a
                        key={`${s.type}-${i}`}
                        className="text-primary underline"
                        href={s.url}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {s.type}: {s.username || 'Profile'} ↗
                      </a>
                    ) : (
                      <span
                        key={`${s.type}-${i}`}
                        className="text-muted-foreground"
                      >
                        {s.type}: {s.username || 'Connected'}
                      </span>
                    ),
                  )}
                </div>
                {p.recentResults.length > 0 && (
                  <div>
                    <p className="font-medium">Recent results · all games</p>
                    <ul className="space-y-1">
                      {p.recentResults.map((r, i) => (
                        <li key={i} className="text-muted-foreground">
                          {r.placement != null ? `#${r.placement}` : 'Unplaced'}
                          {r.entrants ? ` / ${r.entrants}` : ''} ·{' '}
                          {r.url ? (
                            <a
                              href={r.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="underline"
                            >
                              {r.name}
                            </a>
                          ) : (
                            r.name
                          )}{' '}
                          {r.game && `(${r.game})`}
                          {!r.final && ' · Provisional'}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            ))}
            {!profile.participants.length && (
              <p>No public player profiles available.</p>
            )}
            <p className="text-xs text-muted-foreground">
              Profile snapshot: {new Date(profile.refreshedAt).toLocaleString()}
            </p>
          </div>
        </details>
      )}
    </div>
  );
}
